import React, { useState } from 'react';

// Api
import { procurementReportApi } from '@/main/api';

// Services
import { getDefaultMessageResultsFound, getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';

// Types
import { ProcurementReportResponse } from '@/main/models/response';
import { ProcurementReportRequest } from '@/main/models/request';

// Components
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import { KOGTableRow } from '@/main/components/shared/boxGrid/styles';
import { RenderQuoteItemInformation, getTooltipFormatted } from '@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo';
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';
import { renderPartInfoRow } from '@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfoRow';


// Components/Styles Local
import { IGridData } from '../../orderReportScreen';

import * as S from '../../styles'

interface IOrderReportGrid {
  formFilters: ProcurementReportRequest | null;
  gridData: IGridData;
  hasShowMore: boolean;
  setHasShowMore: React.Dispatch<React.SetStateAction<boolean>>;
  setGridData: React.Dispatch<React.SetStateAction<IGridData>>;
  pageIndex: PaginationEnum;
  setPageIndex: React.Dispatch<React.SetStateAction<PaginationEnum>>;
  isLoadingInfiniteScroll: boolean;
  setIsLoadingInfiniteScroll: React.Dispatch<React.SetStateAction<boolean>>;
}
function OrderReportGrid({
  formFilters,
  hasShowMore,
  gridData,
  setHasShowMore,
  setGridData,
  pageIndex,
  setPageIndex,
  isLoadingInfiniteScroll,
  setIsLoadingInfiniteScroll
}: IOrderReportGrid) {


  // ASYNC FUNCTIONS
  function onLoadInfiniteScroll(request: ProcurementReportRequest) {
    setIsLoadingInfiniteScroll(true);

    procurementReportApi
      .searchgrid(request)
      .then(response => {
        let resultData: ProcurementReportResponse[] = [];
        let resultTotalItems: number = 0;
        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }
        if (gridData.data.length > 0) {
          resultData = [...gridData.data, ...resultData];
        }
        setGridData({
          isLoading: false,
          totalItems: resultTotalItems,
          data: resultData
        });

        // Disable show more
        if (
          resultTotalItems < PaginationEnum.PageSize10 ||
          resultTotalItems === resultData.length
        ) {
          setHasShowMore(false);
        }
      })
      .finally(() => setIsLoadingInfiniteScroll(false));
  }

  function onLoadSearchGrid() {
    const index = pageIndex + PaginationEnum.PageIndexIntial;
    setPageIndex(index);
    const formFiltersOnLoadSearchGrid = formFilters;
    if (formFiltersOnLoadSearchGrid) {
      const updatedFormFilters = {
        ...formFiltersOnLoadSearchGrid,
        pageIndex: index
      };
      onLoadInfiniteScroll(updatedFormFilters);
    }
  }

  if (gridData.isLoading) return <LoadingComponent />;
  if (gridData.data.length === 0 && formFilters !== null)
    return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
  if (gridData.data.length === 0 && !gridData.isLoading) return null;

  // CONSTANTS LAYOUT
  const titleGrid = getDefaultMessageResultsFound(
    gridData.data.length,
    gridData.totalItems
  );
  const rowHeaders = [
    getTranslateByKey('information.insurer'),
    getTranslateByKey('information.bodyshop'),
    getTranslateByKey('information.claimNr'),
    getTranslateByKey('information.vehicle'),
    getTranslateByKey('information.licensePlate'),
    getTranslateByKey('information.orderNr'),
    getTranslateByKey('information.newOrderDate'),
    getTranslateByKey('companyType.supplier'),
    getTranslateByKey('information.part'),
    getTranslateByKey('information.status'),
  ];
  return (
    <BoxGrid
      title={titleGrid}
      rowHeaders={rowHeaders}
      infiniteScrollConfig={{
        dataLength: gridData.data.length,
        fetchData: () => onLoadSearchGrid(),
        hasMore: hasShowMore,
        isLoading: isLoadingInfiniteScroll,
      }}>
      {gridData.data.map(part => (
        <KOGTableRow
          key={part.insurerName}
          id={`id-orderReport-tableRow-result-${part.insurerName}`}>
          <S.TableCellGridLarge title={part.insurerName}><S.TableGrid>{part.insurerName}</S.TableGrid></S.TableCellGridLarge>
          <S.TableCellGridLarge title={part.bodyShopName}><S.TableGrid>{part.bodyShopName}</S.TableGrid></S.TableCellGridLarge>
          <S.TableCellGrid title={part.claimNr}><S.TableGrid>{part.claimNr}</S.TableGrid></S.TableCellGrid>
          <S.TableCellGrid title={part.vehicle}><S.TableGrid>{part.vehicle}</S.TableGrid></S.TableCellGrid>
          <S.TableCellGridLicensePlate title={part.licensePlate}><S.TableGrid>{part.licensePlate}</S.TableGrid></S.TableCellGridLicensePlate>
          <S.TableCellGridOrderNr title={isNullUndefined(part.orderNr) ? '' : String(part.orderNr)}><S.TableGrid>{part.orderNr}</S.TableGrid></S.TableCellGridOrderNr>
          <S.TableCellGridOrderDate title={part.orderDate}><S.TableGrid>{part.orderDate}</S.TableGrid></S.TableCellGridOrderDate>
          <S.TableCellGridLarge title={part.supplierName}><S.TableGrid>{part.supplierName}</S.TableGrid></S.TableCellGridLarge>
          <S.TableCellGridLarge>
            {!isNullUndefined(part.quoteProcurementPartInfo) &&
              <TooltipComponent
                title={getTooltipFormatted(part.quoteProcurementPartInfo)}
              >
                <S.QuoteProcurementPartInfoRowTooltip>
                  <RenderQuoteItemInformation
                    quoteItemDescription={
                      part.quoteProcurementPartInfo?.quoteItemDescription
                    }
                    quoteItemPartNumber={
                      part.quoteProcurementPartInfo?.quoteItemPartNumber
                    }
                  />
                  {renderPartInfoRow(part.quoteProcurementPartInfo)}
                </S.QuoteProcurementPartInfoRowTooltip>
              </TooltipComponent>
            }
          </S.TableCellGridLarge>
          <S.TableCellGrid title={part.procurementStatus}><S.TableGrid>{part.procurementStatus}</S.TableGrid></S.TableCellGrid>
        </KOGTableRow>
      ))}
    </BoxGrid>
  )

}

export default OrderReportGrid;